import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Package, Truck, MapPin, CreditCard, Loader2, CheckCircle2, Store, Copy, Check, AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getInfo, getMe, criarPedido, fmtPreco,
  getLojaToken,
} from './lojaApi'
import { useCarrinho } from './useCarrinho'
import LojaLayout from './LojaLayout'

type TipoEntrega = 'retirada' | 'entrega'
type FormaPgto = 'dinheiro' | 'pix' | 'cartao_debito' | 'cartao_credito' | 'boleto' | 'prazo'

const FORMAS_LABELS: Record<FormaPgto, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Cartão de débito',
  cartao_credito: 'Cartão de crédito',
  boleto: 'Boleto',
  prazo: 'A prazo',
}

export default function LojaCheckout() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const token = getLojaToken()
  useEffect(() => {
    if (!token) {
      navigate(`/loja/${slug}/login?redirect=/loja/${slug}/checkout`, { replace: true })
    }
  }, [token, slug, navigate])

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const { data: me } = useQuery({
    queryKey: ['loja-me'],
    queryFn: () => getMe(),
    enabled: !!token,
    retry: false,
  })

  const itens = useCarrinho((s) => s.getItens(slug))
  const totalItens = useCarrinho((s) => s.getTotalItens(slug))
  const subtotal = useCarrinho((s) => s.getTotal(slug))
  const limpar = useCarrinho((s) => s.limpar)

  // Form do checkout
  const [tipoEntrega, setTipoEntrega] = useState<TipoEntrega>('retirada')
  const [formaPagamento, setFormaPagamento] = useState<FormaPgto>('pix')
  const [observacao, setObservacao] = useState('')

  // Endereço
  const [cep, setCep] = useState('')
  const [logradouro, setLogradouro] = useState('')
  const [numero, setNumero] = useState('')
  const [complemento, setComplemento] = useState('')
  const [bairro, setBairro] = useState('')
  const [cidade, setCidade] = useState('')
  const [uf, setUf] = useState('')

  // Preenche endereço do cadastro quando chega
  useEffect(() => {
    if (me?.endereco) {
      setCep(me.endereco.cep || '')
      setLogradouro(me.endereco.logradouro || '')
      setNumero(me.endereco.numero || '')
      setComplemento(me.endereco.complemento || '')
      setBairro(me.endereco.bairro || '')
      setCidade(me.endereco.cidade || '')
      setUf(me.endereco.uf || '')
    }
  }, [me])

  // Se a loja só permite um dos modos, fixa
  useEffect(() => {
    if (info) {
      if (info.permite_retirada && !info.permite_entrega) setTipoEntrega('retirada')
      if (info.permite_entrega && !info.permite_retirada) setTipoEntrega('entrega')
    }
  }, [info])

  const frete = useMemo(() => {
    if (tipoEntrega === 'entrega' && info?.taxa_entrega_fixa) {
      return info.taxa_entrega_fixa
    }
    return 0
  }, [tipoEntrega, info?.taxa_entrega_fixa])

  const total = subtotal + frete

  const [loading, setLoading] = useState(false)

  async function finalizar() {
    if (itens.length === 0) {
      toast.error('Carrinho vazio')
      return
    }
    setLoading(true)
    try {
      const pedido = await criarPedido(slug, {
        itens: itens.map((it) => ({ sku: it.sku, quantidade: it.quantidade })),
        tipo_entrega: tipoEntrega,
        forma_pagamento: formaPagamento,
        parcelas: 1,
        observacao: observacao.trim() || undefined,
        ...(tipoEntrega === 'entrega' ? {
          endereco_cep: cep,
          endereco_logradouro: logradouro,
          endereco_numero: numero,
          endereco_complemento: complemento || undefined,
          endereco_bairro: bairro || undefined,
          endereco_cidade: cidade,
          endereco_uf: uf,
        } : {}),
      })

      // Limpa carrinho
      limpar(slug)
      toast.success(`Pedido #${pedido.numero} criado!`)

      // Vai pra página de rastreio
      navigate(`/loja/${slug}/rastreio/${pedido.rastreio_token}`, { replace: true })
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Erro ao finalizar pedido'
      toast.error(typeof msg === 'string' ? msg : 'Erro ao finalizar pedido')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-md mx-auto p-8 text-center">
          <Loader2 className="mx-auto animate-spin text-ink-400" size={24} />
        </div>
      </LojaLayout>
    )
  }

  if (itens.length === 0) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <div className="card card-body py-10 text-ink-500">
            <Package size={32} className="mx-auto mb-2 text-ink-300" />
            <div className="font-medium text-ink-700 mb-3">Seu carrinho está vazio</div>
            <Link to={`/loja/${slug}`} className="btn-primary inline-flex">
              Ver catálogo
            </Link>
          </div>
        </div>
      </LojaLayout>
    )
  }

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Link to={`/loja/${slug}/carrinho`} className="btn-ghost text-sm mb-4 inline-flex">
          <ArrowLeft size={14} /> Voltar ao carrinho
        </Link>

        <h1 className="text-2xl font-semibold text-ink-900 mb-5">Finalizar pedido</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* === COLUNA ESQUERDA: DADOS === */}
          <div className="space-y-4">
            {/* Tipo de entrega */}
            <div className="card card-body">
              <h2 className="font-semibold text-ink-900 mb-3">Como você quer receber?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {info?.permite_retirada && (
                  <label className={`p-3 border-2 rounded-md cursor-pointer ${
                    tipoEntrega === 'retirada' ? 'border-brand-600 bg-brand-50' : 'border-ink-200 hover:bg-ink-50'
                  }`}>
                    <input
                      type="radio"
                      name="tipo"
                      className="sr-only"
                      checked={tipoEntrega === 'retirada'}
                      onChange={() => setTipoEntrega('retirada')}
                    />
                    <div className="flex items-start gap-2">
                      <Store size={18} className={tipoEntrega === 'retirada' ? 'text-brand-600' : 'text-ink-500'} />
                      <div>
                        <div className="font-medium text-sm">Retirar na loja</div>
                        <div className="text-xs text-ink-500 mt-0.5">
                          {info.endereco?.logradouro}, {info.endereco?.numero} · {info.endereco?.cidade}/{info.endereco?.uf}
                        </div>
                      </div>
                    </div>
                  </label>
                )}
                {info?.permite_entrega && (
                  <label className={`p-3 border-2 rounded-md cursor-pointer ${
                    tipoEntrega === 'entrega' ? 'border-brand-600 bg-brand-50' : 'border-ink-200 hover:bg-ink-50'
                  }`}>
                    <input
                      type="radio"
                      name="tipo"
                      className="sr-only"
                      checked={tipoEntrega === 'entrega'}
                      onChange={() => setTipoEntrega('entrega')}
                    />
                    <div className="flex items-start gap-2">
                      <Truck size={18} className={tipoEntrega === 'entrega' ? 'text-brand-600' : 'text-ink-500'} />
                      <div>
                        <div className="font-medium text-sm">Entrega</div>
                        <div className="text-xs text-ink-500 mt-0.5">
                          {info.taxa_entrega_fixa != null
                            ? `Taxa fixa R$ ${fmtPreco(info.taxa_entrega_fixa)}`
                            : 'Taxa a combinar'}
                        </div>
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Endereço (se entrega) */}
            {tipoEntrega === 'entrega' && (
              <div className="card card-body">
                <h2 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
                  <MapPin size={16} /> Endereço de entrega
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2">
                  <div>
                    <label className="label">CEP</label>
                    <input className="input" value={cep} onChange={e => setCep(e.target.value)} placeholder="00000-000" required />
                  </div>
                  <div>
                    <label className="label">Logradouro</label>
                    <input className="input" value={logradouro} onChange={e => setLogradouro(e.target.value)} required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-2 mt-2">
                  <div>
                    <label className="label">Número</label>
                    <input className="input" value={numero} onChange={e => setNumero(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">Complemento (opcional)</label>
                    <input className="input" value={complemento} onChange={e => setComplemento(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <div>
                    <label className="label">Bairro</label>
                    <input className="input" value={bairro} onChange={e => setBairro(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Cidade</label>
                    <input className="input" value={cidade} onChange={e => setCidade(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">UF</label>
                    <input className="input" value={uf} maxLength={2} onChange={e => setUf(e.target.value.toUpperCase())} required />
                  </div>
                </div>
              </div>
            )}

            {/* Pagamento */}
            <div className="card card-body">
              <h2 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
                <CreditCard size={16} /> Forma de pagamento
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {(Object.keys(FORMAS_LABELS) as FormaPgto[]).map((f) => (
                  <label key={f} className={`p-3 border-2 rounded-md cursor-pointer text-sm ${
                    formaPagamento === f ? 'border-brand-600 bg-brand-50 text-brand-700 font-medium' : 'border-ink-200 hover:bg-ink-50'
                  }`}>
                    <input
                      type="radio"
                      name="forma"
                      className="sr-only"
                      checked={formaPagamento === f}
                      onChange={() => setFormaPagamento(f)}
                    />
                    {FORMAS_LABELS[f]}
                  </label>
                ))}
              </div>
            </div>

            {/* Instruções dinâmicas por forma de pagamento */}
            <InstrucaoFormaPgto
              forma={formaPagamento}
              total={total}
              chavePix={info?.chave_pix || null}
              chavePixNome={info?.chave_pix_nome || null}
              whatsapp={info?.whatsapp || null}
            />

            {/* Observação */}
            <div className="card card-body">
              <label className="label">Observação (opcional)</label>
              <textarea
                className="input min-h-[72px]"
                placeholder="Ex: entregar na portaria, horário preferido, etc"
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
              />
            </div>
          </div>

          {/* === COLUNA DIREITA: RESUMO === */}
          <div className="lg:sticky lg:top-[5rem] self-start">
            <div className="card card-body space-y-3">
              <h2 className="font-semibold text-ink-900">Resumo do pedido</h2>

              <div className="space-y-2 max-h-[300px] overflow-y-auto text-sm">
                {itens.map((it) => (
                  <div key={it.sku} className="flex gap-2 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium line-clamp-1">{it.descricao}</div>
                      <div className="text-xs text-ink-500">
                        {it.quantidade} {it.unidade} × R$ {fmtPreco(it.preco)}
                      </div>
                    </div>
                    <div className="text-ink-900 font-medium shrink-0">
                      R$ {fmtPreco(it.preco * it.quantidade)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-ink-100 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-ink-600">
                  <span>Subtotal ({totalItens} {totalItens === 1 ? 'item' : 'itens'})</span>
                  <span>R$ {fmtPreco(subtotal)}</span>
                </div>
                {tipoEntrega === 'entrega' && (
                  <div className="flex justify-between text-ink-600">
                    <span>Frete</span>
                    <span>
                      {frete > 0 ? `R$ ${fmtPreco(frete)}` : 'A combinar'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-ink-100 mt-2">
                  <span className="font-semibold text-ink-900">Total</span>
                  <span className="text-xl font-bold text-ink-900">R$ {fmtPreco(total)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={finalizar}
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading
                  ? <><Loader2 size={14} className="animate-spin" /> Processando...</>
                  : <><CheckCircle2 size={14} /> Confirmar pedido</>}
              </button>

              <div className="text-[11px] text-ink-500 text-center leading-relaxed">
                Ao confirmar, você concorda com os termos da loja.
                Você receberá um link para acompanhar seu pedido.
              </div>
            </div>
          </div>
        </div>
      </div>
    </LojaLayout>
  )
}


// ============================================================
// INSTRUÇÃO DINÂMICA POR FORMA DE PAGAMENTO
// ============================================================
interface InstrucaoProps {
  forma: FormaPgto
  total: number
  chavePix: string | null
  chavePixNome: string | null
  whatsapp: string | null
}

function InstrucaoFormaPgto({ forma, total, chavePix, chavePixNome, whatsapp }: InstrucaoProps) {
  const [pixCopiado, setPixCopiado] = useState(false)

  function copiarPix() {
    if (!chavePix) return
    navigator.clipboard.writeText(chavePix)
    setPixCopiado(true)
    toast.success('Chave PIX copiada')
    setTimeout(() => setPixCopiado(false), 2500)
  }

  // PIX — tem card especial
  if (forma === 'pix') {
    return (
      <div className="card card-body bg-emerald-50 border-emerald-200">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor"><path d="M16 2 2 16l14 14 14-14zm0 4.24L25.76 16 16 25.76 6.24 16z"/></svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-emerald-900">Pagamento via PIX</div>
            <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
              Após confirmar o pedido, faça o PIX de <strong>R$ {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(total)}</strong> para a chave abaixo.
              O pedido será separado assim que a loja confirmar o recebimento.
            </p>

            {chavePix ? (
              <div className="mt-3 bg-white border border-emerald-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Chave PIX</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-sm font-mono text-ink-900 break-all">{chavePix}</code>
                  <button type="button" onClick={copiarPix} className="btn-ghost text-xs shrink-0">
                    {pixCopiado ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    {pixCopiado ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                {chavePixNome && (
                  <div className="text-[11px] text-ink-500 mt-1">
                    Em nome de: <strong className="text-ink-700">{chavePixNome}</strong>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                <AlertCircle size={12} className="inline mr-1" />
                Loja ainda não configurou chave PIX. Entre em contato pelo WhatsApp para combinar o pagamento.
              </div>
            )}

            {whatsapp && (
              <div className="text-[11px] text-emerald-800 mt-2">
                Após pagar, envie o comprovante pelo WhatsApp para agilizar a confirmação.
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Outras formas — card de info simples
  const configs: Record<FormaPgto, { titulo: string; texto: string; cor: string; bg: string } | null> = {
    dinheiro: {
      titulo: 'Pagamento em dinheiro',
      texto: 'O pagamento será feito no momento da retirada ou entrega. Tenha o valor preparado.',
      cor: 'text-blue-900', bg: 'bg-blue-50 border-blue-200',
    },
    cartao_debito: {
      titulo: 'Pagamento no cartão de débito',
      texto: 'O cartão será passado na maquineta no momento da retirada ou entrega.',
      cor: 'text-blue-900', bg: 'bg-blue-50 border-blue-200',
    },
    cartao_credito: {
      titulo: 'Pagamento no cartão de crédito',
      texto: 'O cartão será passado na maquineta no momento da retirada ou entrega. Combine o parcelamento com o atendente.',
      cor: 'text-blue-900', bg: 'bg-blue-50 border-blue-200',
    },
    boleto: {
      titulo: 'Pagamento via boleto',
      texto: 'O boleto será enviado pelo WhatsApp da loja após a confirmação do pedido. O pedido será separado após a compensação.',
      cor: 'text-amber-900', bg: 'bg-amber-50 border-amber-200',
    },
    prazo: {
      titulo: 'Pagamento a prazo',
      texto: 'O pedido passará por análise de crédito antes de ser aprovado. A loja entrará em contato para confirmar.',
      cor: 'text-amber-900', bg: 'bg-amber-50 border-amber-200',
    },
    pix: null,  // já tratado acima
  }

  const cfg = configs[forma]
  if (!cfg) return null

  return (
    <div className={`card card-body ${cfg.bg}`}>
      <div className={`font-semibold text-sm ${cfg.cor}`}>{cfg.titulo}</div>
      <p className={`text-xs mt-1 ${cfg.cor} opacity-90 leading-relaxed`}>{cfg.texto}</p>
    </div>
  )
}
